"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QuestionnaireEngine = exports.DEFAULT_RENDERING_OPTS = void 0;
const typegate_1 = require("typegate");
const evaluateConditions_1 = require("../../condition-engine/evaluateConditions");
const renderingType_1 = require("../../types/carrierQuestionnaire/renderingType");
const outputMapping_1 = require("../../types/outputMapping/outputMapping");
const computedAnswers_1 = require("../computedAnswers");
const defaultAnswers_1 = require("../defaultAnswers");
const detailedMappingInterpreter_1 = require("../detailedMappingInterpreter/detailedMappingInterpreter");
const dependencyMap_1 = require("../nodeEvaluation/visibleIf/dependencyMap");
const OutputMappingInterpreter_1 = require("../outputMappingInterpreter/OutputMappingInterpreter");
const progress_1 = require("../progress");
const questionnaire_1 = require("../questionnaire");
const filterNodesByApplicationMode_1 = require("../questionnaire/filterNodesByApplicationMode");
const filterNodesByPlatformType_1 = require("../questionnaire/filterNodesByPlatformType");
const filterQuestionnaireSectionsByInsuranceModule_1 = require("../questionnaire/filterQuestionnaireSectionsByInsuranceModule");
const questionnaireScreenExtenderVisitor_1 = require("../questionnaireHelpers/questionnaireScreenExtenderVisitor");
const RenderingQuestionnaireGenerator_1 = require("../renderingTransforms/RenderingQuestionnaireGenerator");
const simpleMappingInterpreter_1 = require("../simpleMappingInterpreter/simpleMappingInterpreter");
const structure_1 = require("../structure");
const updateAnswer_1 = require("../updateAnswer");
const validations_1 = require("../validations");
const shareQuestionnaireReferences_1 = require("./shareQuestionnaireReferences");
exports.DEFAULT_RENDERING_OPTS = {
    activeSectionId: undefined,
    readonly: false,
    displayErrors: false,
    loadingFields: false,
    renderingType: renderingType_1.RenderingType.web,
};
class QuestionnaireEngine {
    questionnaire;
    questionnaireNodes;
    answerResolver;
    visibilityDependencyMap;
    config;
    timezone;
    renderingQuestionnaire;
    renderingQuestionnaireGenerator = null;
    currentDateOverride = null;
    applicationContext;
    onRenderingQuestionnaireChanged;
    answerChangedSubscribers;
    renderingOptions;
    isLoading = false;
    cloningStrategy;
    language;
    validationMessages = undefined;
    constructor(questionnaire, questionnaireNodes, answerResolver, visibilityDependencyMap, config, timezone, applicationContext, onRenderingQuestionnaireChanged, answerChangedSubscribers, renderingOptions, currentDateOverride, cloningStrategy = 'CacheSafe', language, validationMessages) {
        this.questionnaire = questionnaire;
        this.questionnaireNodes = questionnaireNodes;
        this.answerResolver = answerResolver;
        this.visibilityDependencyMap = visibilityDependencyMap;
        this.config = config;
        this.timezone = timezone;
        this.renderingQuestionnaireGenerator = null;
        this.applicationContext = applicationContext;
        this.onRenderingQuestionnaireChanged = onRenderingQuestionnaireChanged;
        this.answerChangedSubscribers = answerChangedSubscribers;
        this.cloningStrategy = cloningStrategy;
        this.language = language;
        this.validationMessages = validationMessages;
        this.renderingOptions = renderingOptions;
        this.currentDateOverride = currentDateOverride;
        if (this.renderingOptions.readonly !== true) {
            (0, defaultAnswers_1.defaultQuestionnaireAnswers)(this.questionnaire, answerResolver, this.visibilityDependencyMap, this.timezone, null);
            (0, computedAnswers_1.computedQuestionnaireAnswers)(this.questionnaire, answerResolver, this.visibilityDependencyMap, this.timezone, null);
        }
        const rq = this.generateRenderingQuestionnaire();
        this.renderingQuestionnaire = rq;
        if (this.renderingOptions.readonly !== true) {
            this.notifyAnswerSubscribers();
        }
    }
    setDisplayError(value) {
        if (this.renderingOptions.displayErrors === value) {
            return;
        }
        this.renderingOptions.displayErrors = value;
        const rq = this.generateRenderingQuestionnaire();
        this.renderingQuestionnaire = rq;
        this.onRenderingQuestionnaireChanged(rq);
    }
    static from(args) {
        const config = args.config || RenderingQuestionnaireGenerator_1.DEFAULT_CONFIG;
        const context = args.context || {};
        const applicationContext = args.applicationContext || {};
        let questionnaire = args.questionnaire;
        const questionnaireNodes = (0, RenderingQuestionnaireGenerator_1.getQuestionnaireNodes)(args.questionnaire);
        const { platformTypes, insuranceModules, applicationModes } = context;
        if (insuranceModules) {
            questionnaire = (0, filterQuestionnaireSectionsByInsuranceModule_1.filterQuestionnaireSectionsByInsuranceModule)(questionnaire, insuranceModules);
        }
        if (platformTypes) {
            questionnaire = (0, filterNodesByPlatformType_1.filterNodesByPlatformType)(questionnaire, platformTypes);
        }
        if (applicationModes) {
            questionnaire = (0, filterNodesByApplicationMode_1.filterNodesByApplicationMode)(questionnaire, applicationModes);
        }
        if (config.screenConfig) {
            questionnaire = (0, questionnaireScreenExtenderVisitor_1.extendQuestionnaireWithScreen)(questionnaire, config.screenConfig);
        }
        const timezone = args.timezone;
        const visibilityDependencyMap = new dependencyMap_1.VisibilityDependencyMap(questionnaire);
        if (args.detailedMappingPrefilledAnswers) {
            const prefillResult = new detailedMappingInterpreter_1.DetailedMappingInterpreter(args.answerResolver).populateAnswers(args.detailedMappingPrefilledAnswers);
            if (!prefillResult.success) {
                throw prefillResult.error;
            }
        }
        const questionnaireEngine = new QuestionnaireEngine(questionnaire, questionnaireNodes, args.answerResolver, visibilityDependencyMap, config, timezone, applicationContext, args.onRenderingQuestionnaireChanged || (() => { }), args.answerChangedSubscribers || [], args.renderingOptions || exports.DEFAULT_RENDERING_OPTS, args.currentDateOverride, args.cloningStrategy, args.language, args.validationMessages);
        return questionnaireEngine;
    }
    clone(versionedAnswers) {
        const answerResolverToUse = this.answerResolver.clone(versionedAnswers);
        return new QuestionnaireEngine(this.questionnaire, this.questionnaireNodes, answerResolverToUse, this.visibilityDependencyMap, this.config, this.timezone, this.applicationContext, this.onRenderingQuestionnaireChanged, this.answerChangedSubscribers, this.renderingOptions, this.currentDateOverride, this.cloningStrategy, this.language, this.validationMessages);
    }
    getAnswerResolverInstance = () => this.answerResolver;
    getAnswer(nodeId, nodeIdScope) {
        return this.answerResolver.usingNodeId().getAnswer(nodeId, nodeIdScope || {});
    }
    getRepeatedAnswers(collectionNodeId, nodeIds, scope) {
        return this.answerResolver.usingNodeId().getRepeatedAnswers(collectionNodeId, nodeIds, scope);
    }
    getAnswersWithDefaultValues() {
        const answerResolverWithDefaults = this.answerResolver.clone();
        (0, defaultAnswers_1.defaultQuestionnaireAnswers)(this.questionnaire, answerResolverWithDefaults, this.visibilityDependencyMap, this.timezone, null);
        (0, computedAnswers_1.computedQuestionnaireAnswers)(this.questionnaire, answerResolverWithDefaults, this.visibilityDependencyMap, this.timezone, null);
        return answerResolverWithDefaults.dump();
    }
    // TODO: (long-term) The questionnaire-engine should not have to expose this function.
    findSectionGroup(sectionGroupId) {
        return this.questionnaire.find((sectionGroup) => sectionGroup.id === sectionGroupId);
    }
    getRepetitionCount(nodeId, scope) {
        return this.answerResolver.usingNodeId().getRepetitionCount(nodeId, scope);
    }
    unsetAnswer(nodeId, scope) {
        if (this.renderingOptions.readonly === true) {
            return false;
        }
        const hasUnsettedAnswer = this.answerResolver.usingNodeId().unsetAnswer(nodeId, scope);
        if (hasUnsettedAnswer) {
            this.generateRenderingQuestionnaire();
            this.onRenderingQuestionnaireChanged(this.renderingQuestionnaire);
            this.notifyAnswerSubscribers([nodeId]);
        }
        return hasUnsettedAnswer;
    }
    removeUndefinedAnswersFromCollection(nodeId, scope) {
        if (this.renderingOptions.readonly === true) {
            return false;
        }
        const removed = this.answerResolver.usingNodeId().removeUndefinedAnswersFromCollection(nodeId, scope);
        if (removed) {
            this.generateRenderingQuestionnaire();
            this.onRenderingQuestionnaireChanged(this.renderingQuestionnaire);
            this.notifyAnswerSubscribers([nodeId]);
        }
        return removed;
    }
    /**
     * Returns a new `Answers` object, with the `newAnswerValue` set at the path that's associated with the `nodeId`.
     * Note: `nodeId` should be used instead of answerPath (Legacy), the latter taking the form of an explicit full path to the answer's key, e.g. `insuredPeople.2.personalInfo.firstName`
     *
     * @example
     * const answers = { name: 'Joe', province: 'QC' };
     * const updatedAnswers = engine.updateAnswer(answers, 'province', 'ON'); // => { name: 'Joe', province: 'ON' }
     */
    updateAnswer(item) {
        if (this.renderingOptions.readonly === true) {
            return;
        }
        (0, updateAnswer_1.updateAnswer)(this.questionnaire, this.answerResolver, this.visibilityDependencyMap, item, this.timezone, this.currentDateOverride, this.applicationContext);
        this.generateRenderingQuestionnaire();
        this.onRenderingQuestionnaireChanged(this.renderingQuestionnaire);
        if (item.tag === 'nodeId') {
            this.notifyAnswerSubscribers([item.nodeId]);
        }
        else {
            const updatedFieldGroupIds = [item.blueprintId];
            if (item.nodeId) {
                updatedFieldGroupIds.push(item.nodeId);
            }
            this.notifyAnswerSubscribers(updatedFieldGroupIds);
        }
        // // Make sure surrogateID of v1 matches surrogateID of V2
        // const surrogateId = answersResolverV1.getInstanceId(nodeId, repeatedIndices || {});
        // if (surrogateId.success && surrogateId.value) {
        //   answersResolverV2.setInstanceId(nodeId, repeatedIndices || {}, surrogateId.value);
        // }
    }
    /**
     * Returns a `RenderingQuestionnaire` object which can be described as a questionnaire that has been evaluated with the given `answers`.
     * This method will decorate each questionnaire's node with additional properties holding the results of the following evaluations:
     * - validity of answers
     * - visibility of sections, subsections, fieldGroups, fields
     * - completion of the questionnaire
     *
     * @example
     * const answers = { name: 'Joe', province: 'QC' };
     * const renderingOptions = {
     *    renderingType: 'web',
     *    displayErrors: true,
     * };
     * const renderingQuestionnaire = engine.generateRenderingQuestionnaire(answers, renderingOptions);
     */
    generateRenderingQuestionnaire() {
        const { renderingType, displayErrors, readonly } = this.renderingOptions;
        this.renderingQuestionnaireGenerator = new RenderingQuestionnaireGenerator_1.RenderingQuestionnaireGenerator({
            questionnaire: this.questionnaire,
            questionnaireNodes: this.questionnaireNodes,
            answersResolver: this.answerResolver,
            applicationContext: this.applicationContext,
            language: this.language,
            displayErrors,
            readonly: readonly,
            renderingType,
            timezone: this.timezone,
            isLoadingFields: this.isLoading,
            currentDateOverride: this.currentDateOverride,
            validationMessages: this.validationMessages,
        }, this.cloningStrategy);
        const nextRenderingQuestionnaire = this.renderingQuestionnaireGenerator.get();
        if (this.renderingQuestionnaire) {
            const merged = (0, shareQuestionnaireReferences_1.shareQuestionnaireReferences)(this.renderingQuestionnaire, nextRenderingQuestionnaire);
            this.renderingQuestionnaire = merged;
            return merged;
        }
        else {
            this.renderingQuestionnaire = nextRenderingQuestionnaire;
            return nextRenderingQuestionnaire;
        }
    }
    setReadonly = (value) => {
        if (this.renderingOptions.readonly == value) {
            return;
        }
        this.renderingOptions.readonly = value;
        if (this.renderingOptions.readonly !== true) {
            (0, defaultAnswers_1.defaultQuestionnaireAnswers)(this.questionnaire, this.answerResolver, this.visibilityDependencyMap, this.timezone, null);
            (0, computedAnswers_1.computedQuestionnaireAnswers)(this.questionnaire, this.answerResolver, this.visibilityDependencyMap, this.timezone, null);
        }
        const rq = this.generateRenderingQuestionnaire();
        this.renderingQuestionnaire = rq;
        this.onRenderingQuestionnaireChanged(rq);
        if (this.renderingOptions.readonly !== true) {
            this.notifyAnswerSubscribers();
        }
    };
    /**
     * Returns a boolean indicating whether the step associated to the stepId is blocking
     */
    isStepBlocking(stepId) {
        const allSubsections = (0, questionnaire_1.getAllSubsections)(this.questionnaire);
        const relevantStep = allSubsections.find((subsection) => subsection.id === stepId) ?? null;
        if (!relevantStep)
            throw Error(`Step not found for id ${stepId}`);
        if (typeof relevantStep.blockedIf !== 'undefined') {
            const isBlocked = (0, evaluateConditions_1.evaluateConditions)(relevantStep.blockedIf, this.answerResolver, {}, this.timezone, this.currentDateOverride).isValid;
            return isBlocked;
        }
        return false;
    }
    /**
     * Returns true if the questionnaire provided with the passed answers is valid and complete, false otherwise
     * @param answers Answers
     * @returns boolean
     */
    isQuestionnaireComplete(answers, opts) {
        const allFieldAnswersAreValidAndComplete = (0, validations_1.areAllFieldsValidAndComplete)(this.questionnaire, this.answerResolver, {
            ...this.config,
            validateAllAnswers: false,
        }, this.timezone, this.currentDateOverride);
        // TODO: DEV-13035 remove the check on blocking rules now that SSQ is deprecated
        if (!opts?.enableBlockedIfConditions) {
            return allFieldAnswersAreValidAndComplete;
        }
        const allSubsections = (0, questionnaire_1.getAllSubsections)(this.questionnaire);
        const noBlockingAnswers = allSubsections.every((subsection) => {
            if ((0, structure_1.isBlockingSubsection)(subsection)) {
                const isBlocked = (0, evaluateConditions_1.evaluateConditions)(subsection.blockedIf, this.answerResolver, {}, this.timezone, this.currentDateOverride).isValid;
                return !isBlocked;
            }
            return true;
        });
        return allFieldAnswersAreValidAndComplete && noBlockingAnswers;
    }
    /**
     * Returns the percentage of progress for the questionnaire provided with the passed answers
     * @param answers Answers
     * @returns number
     */
    calculateProgress(isCompleted, progressOffset, landingStepId) {
        return (0, progress_1.calculateProgress)(this.questionnaire, this.answerResolver, progressOffset, this.timezone, this.currentDateOverride, isCompleted, landingStepId);
    }
    updateAnswers(items) {
        if (this.renderingOptions.readonly === true) {
            return;
        }
        if (items.length <= 0) {
            return;
        }
        const updatedIds = [];
        for (const item of items) {
            (0, updateAnswer_1.updateAnswer)(this.questionnaire, this.answerResolver, this.visibilityDependencyMap, item, this.timezone, this.currentDateOverride, this.applicationContext);
            if (item.tag === 'nodeId') {
                updatedIds.push(item.nodeId);
            }
            else {
                updatedIds.push(item.blueprintId);
                if (item.nodeId) {
                    updatedIds.push(item.nodeId);
                }
            }
        }
        const rq = this.generateRenderingQuestionnaire();
        this.onRenderingQuestionnaireChanged(rq);
        this.notifyAnswerSubscribers(updatedIds);
    }
    createInstanceByNodeId(nodeId, scope) {
        if (this.renderingOptions.readonly === true) {
            return;
        }
        this.answerResolver.usingNodeId().setInstanceId(nodeId, scope);
        const rq = this.generateRenderingQuestionnaire();
        this.onRenderingQuestionnaireChanged(rq);
        this.notifyAnswerSubscribers([nodeId]);
    }
    onBulkAnswerClear = (fields, blueprintIdScope, nodeIdScope, effects) => {
        if (this.renderingOptions.readonly === true) {
            return;
        }
        const updateIds = [];
        for (const field of fields) {
            const item = {
                tag: 'blueprintId',
                blueprintId: field.blueprintId,
                blueprintIdScope: blueprintIdScope || {},
                nodeIdScope: nodeIdScope,
                value: undefined,
                effects,
            };
            (0, updateAnswer_1.updateAnswer)(this.questionnaire, this.answerResolver, this.visibilityDependencyMap, item, this.timezone, this.currentDateOverride, this.applicationContext);
            updateIds.push(field.nodeId || field.blueprintId);
        }
        const rq = this.generateRenderingQuestionnaire();
        this.onRenderingQuestionnaireChanged(rq);
        this.notifyAnswerSubscribers(updateIds);
    };
    removeItemFromCollection(surrogateId, collectionNodeId, collectionSurrogateIdNodeId) {
        if (this.renderingOptions.readonly === true) {
            return;
        }
        const repeatedCollectionAnswers = this.getRepeatedAnswers(collectionNodeId, [collectionSurrogateIdNodeId], {});
        const collectionItem = repeatedCollectionAnswers?.[surrogateId];
        if (!collectionItem) {
            throw new Error(`Could not find item ${surrogateId} from collection '${collectionNodeId}'.`);
        }
        const hasRemovedItem = this.answerResolver.usingNodeId().unsetAnswer(collectionNodeId, {
            [collectionNodeId]: collectionItem.repeatedIndex,
        });
        if (!hasRemovedItem) {
            throw new Error(`Unable to remove item '${surrogateId}' at index '${collectionItem.repeatedIndex}' from collection '${collectionNodeId}'.`);
        }
        this.answerResolver.usingNodeId().removeUndefinedAnswersFromCollection(collectionNodeId);
        const rq = this.generateRenderingQuestionnaire();
        this.onRenderingQuestionnaireChanged(rq);
        this.notifyAnswerSubscribers([collectionSurrogateIdNodeId, collectionNodeId]);
    }
    getOutputFromMappingConfig(outputMappingConfig) {
        const typedOutputMappingConfigResult = outputMapping_1.outputMappingSchema.parse(outputMappingConfig);
        if (!typedOutputMappingConfigResult.success) {
            return (0, typegate_1.failure)(new outputMapping_1.OutputMappingNotMatchingSchemaError('Output config does not match schema', {
                cause: typedOutputMappingConfigResult.error,
            }));
        }
        const interpreter = new OutputMappingInterpreter_1.OutputMappingInterpreter(typedOutputMappingConfigResult.value, this.answerResolver);
        return interpreter.getOutput();
    }
    notifyAnswerSubscribers(nodeIds = []) {
        for (const subscriber of this.answerChangedSubscribers) {
            void subscriber(this, nodeIds);
        }
    }
    getSimpleMappingOutput() {
        return new simpleMappingInterpreter_1.SimpleMappingInterpreter(this.answerResolver).getOutput();
    }
    populateSimpleMappingAnswers(answers) {
        new simpleMappingInterpreter_1.SimpleMappingInterpreter(this.answerResolver).populateAnswers(answers);
        const rq = this.generateRenderingQuestionnaire();
        this.onRenderingQuestionnaireChanged(rq);
    }
    getDetailedMappingOutput(language) {
        return new detailedMappingInterpreter_1.DetailedMappingInterpreter(this.answerResolver).getOutput(language);
    }
}
exports.QuestionnaireEngine = QuestionnaireEngine;
